import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTA from '../components/home/CTA';
import { appendixContent, images } from '../data/mock';

gsap.registerPlugin(ScrollTrigger);

const Appendix = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.appendix-animate',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          delay: 0.3,
        }
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B1121]">
      <Header />
      <main>
        {/* Hero */}
        <section ref={heroRef} className="relative pt-40 pb-20">
          <div className="absolute inset-0">
            <img
              src={images.appendixHero}
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B1121] via-[#0B1121]/90 to-[#0B1121]" />
          </div>

          <div className="relative z-10 l-container">
            <div className="flex items-center gap-4 mb-8 appendix-animate">
              <img src={images.logoSymbol} alt="" className="w-12 h-12 opacity-60" />
            </div>

            <p className="sub-title appendix-animate">{appendixContent.hero.label}</p>
            <h1 className="font-serif text-5xl lg:text-6xl text-white appendix-animate">
              {appendixContent.hero.title}
            </h1>
          </div>
        </section>

        {/* Jobs Section */}
        <JobsSection data={appendixContent.sections[0]} />

        {/* Emissions Section */}
        <EmissionsSection data={appendixContent.sections[1]} />

        {/* Supply Chain Section */}
        <SupplySection data={appendixContent.sections[2]} />

        <CTA />
      </main>
      <Footer />
    </div>
  );
};

const JobsSection = ({ data }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.jobs-animate',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-20">
      <div className="l-container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-4xl text-white mb-8 jobs-animate">
              {data.title}
            </h2>
            <p className="text-white/70 leading-relaxed mb-8 jobs-animate">
              {data.content}
            </p>
            <div className="space-y-4 jobs-animate">
              <p className="text-[#4ECDC4] text-sm">{data.techLabel}</p>
              <p className="text-white/50 text-sm">{data.locationLabel}</p>
            </div>
          </div>
          <div className="jobs-animate">
            <img
              src={images.howIt2}
              alt=""
              className="w-full rounded-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const EmissionsSection = ({ data }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.emissions-animate',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20">
      <div className="absolute inset-0">
        <img
          src={images.greenhouseBg}
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-[#0B1121]/90" />
      </div>

      <div className="relative z-10 l-container">
        <div className="flex items-start gap-8 mb-12">
          <img
            src={images.greenhouse}
            alt=""
            className="w-16 h-16 opacity-80 emissions-animate"
          />
          <h2 className="font-serif text-4xl text-white emissions-animate">
            {data.title}
          </h2>
        </div>

        <div className="max-w-3xl">
          <p className="text-white/70 leading-relaxed mb-8 emissions-animate">
            {data.content}
          </p>
          <p className="text-white/70 leading-relaxed mb-8 emissions-animate">
            {data.graphiteContent}
          </p>
        </div>

        <div className="flex items-center gap-4 mt-12 emissions-animate">
          <img 
            src="/favicon.png" 
            alt="Qasaba Energy" 
            className="w-10 h-10 object-contain opacity-60"
          />
          <p className="text-white/60 text-sm italic">{data.note}</p>
        </div>
      </div>
    </section>
  );
};

const SupplySection = ({ data }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.supply-animate',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 60%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-20">
      <div className="l-container">
        <h2 className="font-serif text-4xl text-white mb-4 supply-animate">
          {data.title}
        </h2>
        <p className="text-[#4ECDC4] text-sm mb-12 supply-animate">{data.intro}</p>

        <p className="text-white/70 leading-relaxed mb-12 max-w-3xl supply-animate">
          {data.content}
        </p>

        {/* Applications Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {data.applications.map((app, index) => (
            <div
              key={index}
              className="supply-animate group relative aspect-square overflow-hidden rounded-lg"
            >
              <img
                src={app.image}
                alt={app.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              <p className="absolute bottom-4 left-4 right-4 text-white text-sm text-center">
                {app.name}
              </p>
            </div>
          ))}
        </div>

        <p className="text-white/60 leading-relaxed supply-animate">
          {data.note}
        </p>
      </div>
    </section>
  );
};

export default Appendix;
